<template>
  <v-container class="billing-page py-8" style="padding: 0px 60px">
    <div class="billing-header mb-10">
      <h1>Billing & Suscripciones</h1>
      <p>Administra tu plan, compra créditos y revisa tu historial de pagos</p>
    </div>

    <v-alert
      v-if="error"
      type="error"
      outlined
      class="mb-6"
    >
      {{ error }}
    </v-alert>

    <div v-if="loading">
      <v-skeleton-loader type="card" class="mb-8" />
      <v-skeleton-loader type="card, card, card" />
    </div>

    <div v-else>
      <section class="mb-12">
        <v-card class="plan-summary-card pa-6 pa-md-8" elevation="2">
          <div class="summary-top">
            <div style="font-family: 'montserrat-bold';">
              <div class="d-flex align-center flex-wrap mb-3">
                <h2 class="summary-title mb-0 mr-3">{{ currentPlan.name }}</h2>
                <v-chip small label color="#1876d2" text-color="white" class="active-chip">
                  {{ currentPlan.status }}
                </v-chip>
              </div>
              <p class="summary-price mb-0">
                ${{ currentPlan.monthlyPrice }} USD / mes
              </p>
            </div>

            <v-btn
              outlined
              class="cancel-button"
              :loading="managingPlan"
              :disabled="managingPlan"
              @click="handleManagePlan"
            >
              Manage your plan
            </v-btn>
          </div>

          <v-row class="mt-7">
            <v-col
              v-for="item in planSummaryItems"
              :key="item.label"
              cols="12"
              md="4"
            >
              <div class="summary-info">
                <div class="summary-icon">
                  <v-icon :color="item.color">{{ item.icon }}</v-icon>
                </div>
                <div>
                  <p class="summary-label mb-1">{{ item.label }}</p>
                  <p class="summary-value mb-0">{{ item.value }}</p>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </section>

      <section class="mb-12">
        <h2 class="section-title mb-7">Planes disponibles</h2>

        <v-row v-if="plans.length">
          <v-col
            v-for="plan in plans"
            :key="plan.id"
            cols="12"
            md="4"
          >
            <v-card
              class="plan-card pa-6"
              :class="{ 'plan-card-current': plan.isCurrent }"
              elevation="2"
            >
              <v-chip
                v-if="plan.isPopular"
                small
                label
                class="floating-badge"
                color="#1876d2"
                text-color="white"
              >
                Más popular
              </v-chip>

              <v-chip
                v-if="plan.isCurrent"
                small
                label
                class="floating-badge current-badge"
                color="#EEF0F4"
                text-color="#111111"
              >
                Plan actual
              </v-chip>

              <div class="plan-icon" :class="`plan-icon-${plan.id}`">
                <v-icon color="white" style="font-size: 32px">{{ plan.icon }}</v-icon>
              </div>

              <h3 class="plan-name">{{ plan.name }}</h3>

              <p class="plan-price">
                ${{ plan.monthlyPrice }}
                <span>USD / mes</span>
              </p>

              <ul class="feature-list">
                <li v-for="feature in plan.features" :key="feature">
                  <v-icon small color="#00A342">mdi-check</v-icon>
                  <span>{{ feature }}</span>
                </li>
              </ul>

              <v-btn
                block
                depressed
                class="primary-gradient-button"
                :loading="changingPlanId === plan.id"
                :disabled="plan.isCurrent || !!changingPlanId || !!buyingPackId"
                @click="handleChangePlan(plan)"
              >
                {{ plan.isCurrent ? 'Plan actual' : activeSubscription ? 'Administrar cambio' : 'Cambiar a este plan' }}
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <v-card v-else class="empty-card pa-6" outlined>
          No hay planes disponibles por el momento.
        </v-card>
      </section>

      <section class="mb-12">
        <h2 class="section-title mb-3">Comprar créditos adicionales</h2>
        <p class="section-subtitle mb-7">
          Los créditos comprados no expiran y se acumulan
        </p>

        <v-row v-if="creditPackages.length">
          <v-col
            v-for="packageItem in creditPackages"
            :key="packageItem.id"
            cols="12"
            md="4"
          >
            <v-card
              class="credit-card pa-6"
              :class="{ 'credit-card-featured': packageItem.featured }"
              elevation="2"
            >
              <v-chip
                v-if="packageItem.featured"
                small
                label
                class="floating-badge"
                color="#1876d2"
                text-color="white"
              >
                Mejor valor
              </v-chip>

              <p class="credits-count">
                {{ formatNumber(packageItem.credits) }}
                <span>créditos</span>
              </p>

              <p class="price-per-credit">
                ${{ packageItem.pricePerCredit }} por crédito
              </p>

              <p class="credit-total">
                ${{ packageItem.totalPrice }}
                <span>USD</span>
              </p>

              <v-btn
                block
                depressed
                class="credits-button"
                :loading="buyingPackId === packageItem.id"
                :disabled="!!buyingPackId || !!changingPlanId"
                @click="handleBuyCredits(packageItem)"
              >
                Comprar créditos
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <v-card v-else class="empty-card pa-6" outlined>
          No hay paquetes de créditos disponibles.
        </v-card>
      </section>

      <section>
        <h2 class="section-title mb-7">Historial de pagos</h2>

        <v-card v-if="paymentHistory.length" class="payment-card pa-5" elevation="2">
          <div
            v-for="(payment, index) in paymentHistory"
            :key="payment.id"
            class="payment-row"
            :class="{ 'with-border': index < paymentHistory.length - 1 }"
          >
            <div class="payment-left">
              <div class="payment-status-icon">
                <v-icon color="#00A342">mdi-check</v-icon>
              </div>
              <div>
                <h3>{{ payment.title }}</h3>
                <p>{{ payment.date }}</p>
              </div>
            </div>

            <div class="payment-right">
              <strong>${{ payment.amount }} {{ payment.currency }}</strong>
              <v-chip small label color="#EEF0F4" text-color="#111111">
                {{ payment.status }}
              </v-chip>
            </div>
          </div>
        </v-card>

        <v-card v-else class="empty-card pa-6" outlined>
          Todavía no hay pagos registrados.
        </v-card>
      </section>
    </div>
  </v-container>
</template>

<script>
export default {
  name: 'Bills',
  data() {
    return {
      loading: true,
      error: null,
      managingPlan: false,
      changingPlanId: null,
      buyingPackId: null,
      currentPlan: {
        name: 'Sin plan activo',
        status: 'Inactivo',
        monthlyPrice: 0,
        renewalDate: 'N/A',
        includedCredits: 0,
        paymentMethodLast4: 'N/A',
      },
      plans: [],
      creditPackages: [],
      paymentHistory: [],
      subscription: null,
    };
  },
  computed: {
    activeSubscription() {
      const status = String(this.subscription?.status || '').toLowerCase();
      return ['active', 'trialing', 'past_due', 'incomplete'].includes(status);
    },
    planSummaryItems() {
      return [
        {
          label: 'Próxima renovación',
          value: this.currentPlan.renewalDate,
          icon: 'mdi-clock-outline',
          color: '#2163FF',
        },
        {
          label: 'Créditos mensuales',
          value: `${this.formatNumber(this.currentPlan.includedCredits)} créditos`,
          icon: 'mdi-creation-outline',
          color: '#1876D2',
        },
        {
          label: 'Método de pago',
          value: `•••• ${this.currentPlan.paymentMethodLast4}`,
          icon: 'mdi-credit-card-outline',
          color: '#00A342',
        },
      ];
    },
  },
  methods: {
    getLanguage() {
      return this.$store?.state?.language || localStorage.getItem('lang') || 'en';
    },
    getAuthConfig() {
      return {
        headers: {
          Authorization: this.$store.state.sToken
            ? `Bearer ${this.$store.state.sToken}`
            : '',
        },
      };
    },
    apiUrl(path) {
      return `${URI}/api/v1/${this.getLanguage()}/billing${path}`;
    },
    getFrontendBaseUrl() {
      return window.location.origin;
    },
    formatNumber(value) {
      return Number(value).toLocaleString('en-US');
    },
    formatDate(value) {
      if (!value) return 'N/A';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    mapStatus(status) {
      const normalized = String(status || '').toLowerCase();
      if (['active', 'trialing'].includes(normalized)) return 'Activo';
      if (['past_due', 'incomplete'].includes(normalized)) return 'Pendiente';
      if (['canceled', 'unpaid', 'incomplete_expired'].includes(normalized)) return 'Inactivo';
      return 'Inactivo';
    },
    mapTxStatus(status) {
      const normalized = String(status || '').toLowerCase();
      if (normalized === 'paid') return 'Pagado';
      if (normalized === 'pending') return 'Pendiente';
      if (normalized === 'failed') return 'Fallido';
      if (normalized === 'refunded') return 'Reembolsado';
      return status || 'N/A';
    },
    planFeatures(plan) {
      const credits = plan.monthlyCreditsConfig || {};
      const modules = plan.modulesConfig || {};
      const features = [];
      const totalCredits = Object.values(credits).reduce((sum, value) => sum + Number(value || 0), 0);
      if (totalCredits > 0) {
        features.push(`${this.formatNumber(totalCredits)} créditos mensuales`);
      }
      const activeModules = Object.entries(modules).filter(([, enabled]) => !!enabled);
      if (activeModules.length > 0) {
        features.push(`${activeModules.length} módulos activos`);
      }
      if (features.length === 0) {
        features.push('Acceso a funcionalidades base');
      }
      return features;
    },
    mapPlans(rawPlans, subscriptionPlanId) {
      const icons = ['mdi-flash-outline', 'mdi-crown-outline', 'mdi-creation-outline'];
      return rawPlans.map((plan, index) => ({
        id: plan.id,
        code: plan.code,
        icon: icons[index % icons.length],
        name: plan.name,
        monthlyPrice: Number(plan.amountCents || 0) / 100,
        currency: (plan.currency || 'usd').toUpperCase(),
        features: this.planFeatures(plan),
        isPopular: index === 1,
        isCurrent: String(subscriptionPlanId || '') === String(plan.id || ''),
        monthlyCreditsConfig: plan.monthlyCreditsConfig || {},
      }));
    },
    mapCreditPacks(rawPacks) {
      return rawPacks.map((pack, index) => {
        const credits = Object.values(pack.creditsConfig || {}).reduce((sum, value) => sum + Number(value || 0), 0);
        const totalPrice = Number(pack.amountCents || 0) / 100;
        const pricePerCredit = credits > 0 ? (totalPrice / credits).toFixed(3) : '0.000';
        return {
          id: pack.id,
          code: pack.code,
          credits,
          pricePerCredit,
          totalPrice,
          featured: index === 1,
        };
      });
    },
    setCurrentPlan(plans, subscription) {
      const activePlan = plans.find((plan) => plan.isCurrent);
      const monthlyCredits = Object.values(activePlan?.monthlyCreditsConfig || {}).reduce(
        (sum, value) => sum + Number(value || 0),
        0,
      );
      this.currentPlan = {
        name: activePlan ? activePlan.name : 'Sin plan activo',
        status: subscription?.status ? this.mapStatus(subscription.status) : 'Inactivo',
        monthlyPrice: activePlan ? activePlan.monthlyPrice : 0,
        renewalDate: this.formatDate(subscription?.currentPeriodEnd),
        includedCredits: monthlyCredits,
        paymentMethodLast4: 'N/A',
      };
    },
    mapPaymentHistory(transactions) {
      return (transactions || []).map((tx) => ({
        id: tx.id,
        title: tx.description || `Cargo ${tx.transactionType || 'billing'}`,
        date: this.formatDate(tx.paidAt || tx.createdAt),
        amount: Number(tx.amountCents || 0) / 100,
        currency: (tx.currency || 'usd').toUpperCase(),
        status: this.mapTxStatus(tx.status),
      }));
    },
    async reconcileFromQueryIfNeeded(config) {
      const checkout = this.$route.query?.checkout;
      const sessionId = this.$route.query?.session_id;
      if (checkout !== 'success' || !sessionId) return;
      try {
        await DB.post(this.apiUrl('/reconcile-checkout-success'), { sessionId }, config);
      } catch (err) {
        const message = err?.response?.data?.message || 'No se pudo reconciliar el pago de Stripe';
        this.error = message;
      }
    },
    async loadBilling() {
      this.loading = true;
      this.error = null;
      try {
        const config = this.getAuthConfig();
        await this.reconcileFromQueryIfNeeded(config);
        const [plansRes, packsRes, statusRes, transactionsRes] = await Promise.all([
          DB.get(this.apiUrl('/plans'), config),
          DB.get(this.apiUrl('/credit-packs'), config),
          DB.get(this.apiUrl('/status'), config),
          DB.get(this.apiUrl('/transactions?limit=20'), config),
        ]);

        const subscription = statusRes.data.subscription;
        this.subscription = subscription;
        this.plans = this.mapPlans(plansRes.data.plans || [], subscription?.planId);
        this.creditPackages = this.mapCreditPacks(packsRes.data.creditPacks || []);
        this.paymentHistory = this.mapPaymentHistory(transactionsRes.data?.transactions || []);
        this.setCurrentPlan(this.plans, subscription);
      } catch (err) {
        this.error = err?.response?.data?.message || err.message || 'No se pudo cargar billing';
      } finally {
        this.loading = false;
      }
    },
    async handleManagePlan() {
      this.managingPlan = true;
      this.error = null;
      try {
        const response = await DB.post(
          this.apiUrl('/customer-portal'),
          { frontendBaseUrl: this.getFrontendBaseUrl() },
          this.getAuthConfig(),
        );
        const url = response?.data?.url;
        if (!url) throw new Error('No se recibió URL del portal');
        window.location.href = url;
      } catch (err) {
        this.error = err?.response?.data?.message || 'No se pudo abrir el portal de Stripe';
      } finally {
        this.managingPlan = false;
      }
    },
    async handleChangePlan(plan) {
      this.changingPlanId = plan.id;
      this.error = null;
      try {
        if (this.activeSubscription) {
          await this.handleManagePlan();
          return;
        }
        const response = await DB.post(
          this.apiUrl('/subscription-checkout'),
          {
            planCode: plan.code,
            frontendBaseUrl: this.getFrontendBaseUrl(),
          },
          this.getAuthConfig(),
        );
        const checkoutUrl = response?.data?.checkoutUrl;
        if (!checkoutUrl) throw new Error('No se recibió checkoutUrl');
        window.location.href = checkoutUrl;
      } catch (err) {
        this.error = err?.response?.data?.message || 'No se pudo iniciar el checkout de suscripción';
      } finally {
        this.changingPlanId = null;
      }
    },
    async handleBuyCredits(packageItem) {
      this.buyingPackId = packageItem.id;
      this.error = null;
      try {
        const response = await DB.post(
          this.apiUrl('/credit-pack-checkout'),
          {
            packCode: packageItem.code,
            frontendBaseUrl: this.getFrontendBaseUrl(),
          },
          this.getAuthConfig(),
        );
        const checkoutUrl = response?.data?.checkoutUrl;
        if (!checkoutUrl) throw new Error('No se recibió checkoutUrl');
        window.location.href = checkoutUrl;
      } catch (err) {
        this.error = err?.response?.data?.message || 'No se pudo iniciar la compra de créditos';
      } finally {
        this.buyingPackId = null;
      }
    },
  },
  mounted() {
    this.loadBilling();
  },
};
</script>

<style scoped>
.billing-page {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0c0d10;
}

.billing-header h1 {
  font-family: 'montserrat-bold';
  font-size: 30px;
  line-height: 1.15;
  margin-bottom: 14px;
  color: #0c0d10;
}

.billing-header p,
.section-subtitle {
  font-family: 'montserrat-semibold';
  font-size: 22px;
  color: #465266;
}

.section-title {
  font-family: 'montserrat-bold';
  font-size: 32px;
  line-height: 1.2;
  color: #0c0d10;
}

.plan-summary-card,
.plan-card,
.credit-card,
.payment-card,
.empty-card {
  border-radius: 16px;
}

.plan-summary-card {
  background: #f0f2ff;
}

.summary-top {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.summary-title {
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.summary-price {
  font-family: 'montserrat-semibold';
  font-size: 23px;
  color: #465266;
}

.active-chip,
.floating-badge {
  font-family: 'montserrat-bold';
}

.cancel-button {
  min-width: 230px;
  height: 48px !important;
  border-color: #d8d9df;
  border-radius: 8px;
  background: white;
  font-family: 'montserrat-bold';
  font-size: 16px;
  color: #111111;
  text-transform: none;
}

.summary-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: white;
}

.summary-label {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
}

.summary-value {
  font-family: 'montserrat-bold';
  font-size: 21px;
  color: #0c0d10;
}

.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 725px;
  border: 2px solid transparent;
  background: white;
}

.plan-card-current {
  border-color: #2163ff;
}

.floating-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
}

.current-badge {
  left: auto;
  right: 20px;
  transform: none;
}

.plan-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 28px;
  border-radius: 16px;
}

.plan-icon-starter {
  background: #0c86d5;
}

.plan-icon-growth {
  background: #1876d2;
}

.plan-icon-enterprise {
  background: #1f5fa6;
}

.plan-name {
  font-family: 'montserrat-bold';
  font-size: 28px;
  margin-bottom: 16px;
}

.plan-price {
  font-family: 'montserrat-bold';
  font-size: 40px;
  margin-bottom: 62px;
}

.plan-price span,
.credit-total span,
.credits-count span {
  font-family: 'montserrat-semibold';
  font-size: 21px;
  color: #465266;
}

.feature-list {
  flex: 1;
  padding: 0;
  margin: 0 0 26px;
  list-style: none;
}

.feature-list li {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 21px;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #2f3b4f;
}

.primary-gradient-button,
.credits-button {
  max-height: 48px !important;
  border-radius: 8px;
  background: #1876d2;
  color: white !important;
  font-family: 'montserrat-bold';
  font-size: 16px;
  text-transform: none;
}

.primary-gradient-button.v-btn--disabled {
  background: #f0f0f3 !important;
  color: #a8afb8 !important;
}

.credit-card {
  position: relative;
  min-height: 350px;
  border: 2px solid transparent;
  background: white;
}

.credit-card-featured {
  border-color: #8a22f5;
}

.credits-count {
  font-family: 'montserrat-bold';
  font-size: 36px;
  margin-bottom: 8px;
}

.price-per-credit {
  font-family: 'montserrat-semibold';
  font-size: 19px;
  color: #626d80;
  margin-bottom: 58px;
}

.credit-total {
  font-family: 'montserrat-bold';
  font-size: 40px;
  margin-bottom: 50px;
}

.credits-button {
  background: #1876d2;
}

.payment-card {
  background: white;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 30px 12px;
}

.payment-row.with-border {
  border-bottom: 1px solid #edf0f3;
}

.payment-left {
  display: flex;
  align-items: center;
  gap: 22px;
}

.payment-status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #d9fae4;
}

.payment-left h3 {
  font-family: 'montserrat-bold';
  font-size: 21px;
  margin-bottom: 5px;
}

.payment-left p {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  margin: 0;
}

.payment-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.payment-right strong {
  font-family: 'montserrat-bold';
  font-size: 21px;
}

.empty-card {
  font-family: 'montserrat-semibold';
  color: #626d80;
  background: white;
}

@media (max-width: 960px) {
  .billing-page {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .billing-header h1 {
    font-size: 32px;
  }

  .billing-header p,
  .section-subtitle {
    font-size: 18px;
  }

  .section-title {
    font-size: 27px;
  }

  .summary-top,
  .payment-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .cancel-button {
    width: 100%;
  }

  .plan-card {
    min-height: auto;
  }

  .plan-price {
    margin-bottom: 36px;
  }

  .payment-right {
    width: 100%;
    align-items: flex-start;
    padding-left: 74px;
  }
}

/* Fresher AI shared visual language */
.billing-page {
  width: 100%;
  max-width: 1740px !important;
  margin: 0 auto;
  padding: 28px 40px 32px !important;
  font-family: 'montserrat-regular';
}

.billing-header h1 {
  margin-bottom: 5px;
  font-size: 28px;
  line-height: 1.2;
}

.billing-header p,
.section-subtitle {
  font-family: 'montserrat-regular';
  font-size: 14px;
  color: #718096;
}

.section-title {
  font-size: 21px;
}

.plan-summary-card,
.plan-card,
.credit-card,
.payment-card,
.empty-card {
  border: 1px solid #e2e7ed !important;
  border-radius: 12px !important;
  box-shadow: 0 3px 12px rgba(30, 47, 70, 0.04) !important;
}

.plan-summary-card {
  background: #f5f9fd;
}

.plan-card,
.credit-card {
  min-height: auto;
}

.plan-name {
  font-size: 21px;
}

.plan-price,
.credit-total {
  margin-bottom: 30px;
  font-size: 32px;
}

.credits-count {
  font-size: 30px;
}

.plan-price span,
.credit-total span,
.credits-count span,
.price-per-credit {
  font-size: 14px;
}

.feature-list li {
  margin-bottom: 14px;
  font-family: 'montserrat-regular';
  font-size: 14px;
}

.primary-gradient-button,
.credits-button {
  height: 42px !important;
  background: #1876d2 !important;
  font-size: 13px;
}

.credit-card-featured {
  border-color: #1876d2 !important;
}

@media (max-width: 960px) {
  .billing-page {
    padding: 24px !important;
  }

  .billing-header h1 {
    font-size: 26px;
  }

  .billing-header p,
  .section-subtitle {
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .billing-page {
    padding: 20px 16px 28px !important;
  }
}
</style>
